import { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';

const typeColors = {
  practice: '#4A6B3E',
  match: '#7A1C1C',
  social: '#B8935A',
  clubhouse_booking: '#55504A',
};

const empty = {
  title: '', type: 'practice', start: '', end: '', allDay: false,
  location: '', description: '', isPublic: true, teamIds: [],
  opponent: '', homeAway: 'home', homeScore: '', awayScore: '',
  season: '', matchStatus: 'scheduled',
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [e, t] = await Promise.all([Admin.events.list(), Admin.teams.list()]);
    setEvents(e.events); setTeams(t.teams);
  }
  useEffect(() => { refresh(); }, []);

  function openCreate(defaults = {}) {
    setForm({ ...empty, ...defaults });
    setModal('create');
  }
  function openEdit(e) {
    setForm({
      title: e.title, type: e.type,
      start: e.start.slice(0, 16), end: e.end.slice(0, 16),
      allDay: !!e.allDay, location: e.location || '', description: e.description || '',
      isPublic: !!e.isPublic, teamIds: e.teamIds || [],
      opponent: e.opponent || '',
      homeAway: e.homeAway || 'home',
      homeScore: e.homeScore ?? '',
      awayScore: e.awayScore ?? '',
      season: e.season || '',
      matchStatus: e.matchStatus || 'scheduled',
    });
    setModal({ id: e.id });
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const {
        opponent, homeAway, homeScore, awayScore, season, matchStatus,
        ...base
      } = form;
      const payload = {
        ...base,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
      };
      if (form.type === 'match') {
        if (opponent) payload.opponent = opponent;
        if (homeAway) payload.homeAway = homeAway;
        if (homeScore !== '' && homeScore !== null) payload.homeScore = Number(homeScore);
        if (awayScore !== '' && awayScore !== null) payload.awayScore = Number(awayScore);
        if (season) payload.season = season;
        if (matchStatus) payload.matchStatus = matchStatus;
      }
      if (modal === 'create') await Admin.events.create(payload);
      else await Admin.events.update(modal.id, payload);
      setModal(null);
      await refresh();
    } finally { setBusy(false); }
  }

  async function remove(id) { await Admin.events.remove(id); await refresh(); }

  return (
    <div>
      <PageHeader
        eyebrow="Calendar"
        title="Events"
        description="Click a date to add. Click an event to edit."
        action={<button onClick={() => openCreate()} className="btn-primary btn-sm">+ New event</button>}
      />
      <div className="card p-3 md:p-5">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={window.innerWidth < 768 ? 'listWeek' : 'dayGridMonth'}
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,listWeek' }}
          events={events.map((e) => ({
            id: e.id, title: e.title, start: e.start, end: e.end, allDay: e.allDay,
            backgroundColor: typeColors[e.type] || '#1A1612', borderColor: typeColors[e.type] || '#1A1612',
          }))}
          eventClick={(info) => {
            const e = events.find((x) => x.id === info.event.id);
            if (e) openEdit(e);
          }}
          dateClick={(info) => {
            const iso = info.dateStr.length <= 10 ? `${info.dateStr}T18:00` : info.dateStr.slice(0, 16);
            const end = new Date(new Date(iso).getTime() + 90 * 60000).toISOString().slice(0, 16);
            openCreate({ start: iso, end });
          }}
          selectable
          height="auto"
          dayMaxEvents={3}
        />
      </div>

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New' : 'Edit'}
        title={modal === 'create' ? 'Create event' : form.title || 'Edit event'}
        size="lg"
        footer={
          <>
            {modal !== 'create' && (
              <button type="button" className="btn-ghost text-danger mr-auto" onClick={() => setConfirm(true)}>
                Delete
              </button>
            )}
            <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('event-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <form id="event-form" onSubmit={save} className="space-y-4">
          <FormField label="Title" required>
            <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus />
          </FormField>
          <div className="grid sm:grid-cols-3 gap-3">
            <FormField label="Type">
              <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="practice">Practice</option>
                <option value="match">Match</option>
                <option value="social">Social</option>
                <option value="clubhouse_booking">Clubhouse booking</option>
              </select>
            </FormField>
            <FormField label="Start" required>
              <input type="datetime-local" className="input" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} required />
            </FormField>
            <FormField label="End" required>
              <input type="datetime-local" className="input" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} required />
            </FormField>
          </div>
          <FormField label="Location">
            <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          {form.type === 'match' && (
            <div className="rounded border border-line bg-line-soft/40 p-3 space-y-3">
              <div className="text-sm font-medium text-ink-2">Match details</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Opponent">
                  <input className="input" value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} placeholder="e.g. Lions RFC" />
                </FormField>
                <FormField label="Home / Away">
                  <select className="input" value={form.homeAway} onChange={(e) => setForm({ ...form, homeAway: e.target.value })}>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </FormField>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <FormField label="Home score">
                  <input type="number" min="0" className="input" value={form.homeScore} onChange={(e) => setForm({ ...form, homeScore: e.target.value })} />
                </FormField>
                <FormField label="Away score">
                  <input type="number" min="0" className="input" value={form.awayScore} onChange={(e) => setForm({ ...form, awayScore: e.target.value })} />
                </FormField>
                <FormField label="Status">
                  <select className="input" value={form.matchStatus} onChange={(e) => setForm({ ...form, matchStatus: e.target.value })}>
                    <option value="scheduled">Scheduled</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </FormField>
              </div>
              <FormField label="Season">
                <input className="input" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="e.g. 2025–26" />
              </FormField>
            </div>
          )}
          {teams.length > 0 && (
            <div>
              <div className="label">Teams</div>
              <div className="flex flex-wrap gap-2">
                {teams.map((t) => {
                  const on = form.teamIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, teamIds: on ? f.teamIds.filter((x) => x !== t.id) : [...f.teamIds, t.id] }))}
                      className={`chip ${on ? 'bg-brand text-white' : 'bg-line-soft text-ink-2 hover:bg-line'}`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="pt-2 border-t border-line">
            <Switch
              checked={form.isPublic}
              onChange={(v) => setForm({ ...form, isPublic: v })}
              label="Visible on public schedule"
              hint="Off for private events like coach-only meetings"
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirm === true}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          if (modal && modal.id) {
            await remove(modal.id);
            setModal(null);
          }
        }}
        title="Delete event?"
        message="This can't be undone."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
